import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { claim_leaderboard_reward, get_leaderboard, get_daily_leaderboard } from 'services/db-service';
import Skeleton from 'react-loading-skeleton';
import { VerifiedIcon } from 'components/VerifiedIcon';
import { useAuth } from '../../helpers/authContext';
import { useToast } from 'components/ui/use-toast';
import { Button, Col, Row } from 'reactstrap';
import { SEO } from 'components/SEO';
import AdSense from 'components/AdSense';
import PostVideoItem from 'features/posts/postVideoItem';

/* ── Types ─────────────────────────────────────────────────────────── */
interface LeaderEntry {
    rank: number;
    username: string;
    total_views: number;
    video_count: number;
    videos_watched: number;
    score: number;
    plan_type?: string;
    reward_amount?: number;
    is_claimed?: boolean;
}

type SortKey = 'score' | 'total_views' | 'video_count' | 'videos_watched';

/* ── Helpers ────────────────────────────────────────────────────────── */
function getUntilNextMonday(): string {
    const now = new Date();
    const nextMonday = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + (7 - (now.getUTCDay() || 7) + 1),
        0, 0, 0
    ));
    const diff = nextMonday.getTime() - now.getTime();
    if (diff <= 0) return '0d 0h 0m 0s';

    const d = Math.floor(diff / (1000 * 60 * 60 * 24));
    const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const m = Math.floor((diff / (1000 * 60)) % 60);
    const s = Math.floor((diff / 1000) % 60);

    return `${d}d ${h}h ${m}m ${s}s`;
}

function getWeekStart(offset = 0): string {
    const now = new Date();
    const dayOfWeek = now.getUTCDay();
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() - daysSinceMonday + offset * 7
    ));
    return monday.toISOString().slice(0, 10);
}

function formatWeekLabel(start: string): string {
    const s = new Date(start + 'T00:00:00Z');
    const e = new Date(s);
    e.setUTCDate(e.getUTCDate() + 6);
    const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    return `${fmt(s)} – ${fmt(e)}, ${e.getUTCFullYear()}`;
}

function formatDailyDateLabel(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00Z');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function getUntilNextUTCDay(): string {
    const now = new Date();
    const nextDay = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + 1,
        0, 0, 0
    ));
    const diff = nextDay.getTime() - now.getTime();
    if (diff <= 0) return '0h 0m 0s';

    const h = Math.floor(diff / (1000 * 60 * 60));
    const m = Math.floor((diff / (1000 * 60)) % 60);
    const s = Math.floor((diff / 1000) % 60);

    return `${h}h ${m}m ${s}s`;
}

const getTodayUTC = () => new Date().toISOString().slice(0, 10);

const MEDALS = ['🥇', '🥈', '🥉'];
const PODIUM_COLORS = ['gold', 'silver', 'bronze'];
const SCORE_BREAKDOWN = (e: LeaderEntry) =>
    `${e.total_views} views × 3 + ${e.video_count} videos × 5 + ${e.videos_watched} watched × 1`;

/* ══════════════════════════════════════════════════════════════════════ */
interface LeaderboardProps {
    tab?: 'weekly' | 'daily';
}

const LeaderboardPage: React.FC<LeaderboardProps> = ({ tab: initialTab }) => {
    const { user } = useAuth();
    const { toast } = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams<{ tab?: string; date?: string }>();

    const activeTab = (params.tab === 'daily' || initialTab === 'daily' || location.pathname.includes('/leaderboard/daily')) ? 'daily' : 'weekly';

    /* ── Weekly State ── */
    const [weekOffset, setWeekOffset] = useState(0);
    const [entries, setEntries] = useState<LeaderEntry[]>([]);
    const [filtered, setFiltered] = useState<LeaderEntry[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [sortBy, setSortBy] = useState<SortKey>('score');
    const [search, setSearch] = useState('');
    const [timeUntil, setTimeUntil] = useState(getUntilNextMonday());
    const [rewardsEnabled, setRewardsEnabled] = useState(false);
    const [prizeSymbol, setPrizeSymbol] = useState('STEEM');
    const [isClaiming, setIsClaiming] = useState(false);

    /* ── Daily State ── */
    const [dailyDate, setDailyDate] = useState<string>(params.date || getTodayUTC());
    const [dailyVideos, setDailyVideos] = useState<any[]>([]);
    const [isDailyLoading, setIsDailyLoading] = useState<boolean>(true);
    const [dailySearch, setDailySearch] = useState<string>('');
    const [dailyTimeUntil, setDailyTimeUntil] = useState<string>(getUntilNextUTCDay());

    // Keep dailyDate in sync if URL param changes
    useEffect(() => {
        if (params.date && params.date !== dailyDate) {
            setDailyDate(params.date);
        }
    }, [params.date]);

    const weekStart = getWeekStart(weekOffset);
    const weekLabel = formatWeekLabel(weekStart);
    const isCurrentWeek = weekOffset === 0;
    const isDailyToday = dailyDate === getTodayUTC();

    /* ── Weekly Timer ── */
    useEffect(() => {
        if (!isCurrentWeek) return;
        const timer = setInterval(() => setTimeUntil(getUntilNextMonday()), 1000);
        return () => clearInterval(timer);
    }, [isCurrentWeek]);

    /* ── Daily Timer ── */
    useEffect(() => {
        if (!isDailyToday) return;
        const timer = setInterval(() => setDailyTimeUntil(getUntilNextUTCDay()), 1000);
        return () => clearInterval(timer);
    }, [isDailyToday]);

    /* ── Fetch Weekly ── */
    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await get_leaderboard(weekStart);
            setEntries(data.entries || []);
            setRewardsEnabled(data.rewards_enabled || false);
            setPrizeSymbol(data.prize_symbol || 'STEEM');
        } catch (e) {
            console.error('[Leaderboard] fetch error', e);
            setEntries([]);
        } finally {
            setIsLoading(false);
        }
    }, [weekStart]);

    /* ── Fetch Daily ── */
    const loadDaily = useCallback(async () => {
        setIsDailyLoading(true);
        try {
            const data = await get_daily_leaderboard(dailyDate);
            setDailyVideos(data.videos || []);
        } catch (e) {
            console.error('[Leaderboard] daily fetch error', e);
            setDailyVideos([]);
        } finally {
            setIsDailyLoading(false);
        }
    }, [dailyDate]);

    useEffect(() => {
        if (activeTab === 'weekly') {
            load();
        } else {
            loadDaily();
        }
    }, [activeTab, load, loadDaily]);

    const handleClaim = async () => {
        if (!user) return;
        setIsClaiming(true);
        try {
            const res = await claim_leaderboard_reward(user, weekStart);
            if (res.success) {
                toast({ title: "Reward Claimed!", description: `Transaction: ${res.tx_id}` });
                load();
            } else {
                toast({ title: "Claim Failed", description: res.error, variant: "destructive" });
            }
        } catch (err) {
            toast({ title: "Error", description: "Something went wrong. Try again.", variant: "destructive" });
        } finally {
            setIsClaiming(false);
        }
    };

    /* ── Filter + sort Weekly ── */
    useEffect(() => {
        let list = [...entries];
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(e => e.username.toLowerCase().includes(q));
        }
        list.sort((a, b) => {
            const valA = a[sortBy] as number;
            const valB = b[sortBy] as number;
            return valB - valA;
        });
        setFiltered(list);
    }, [entries, search, sortBy]);

    const handleSwitchTab = (newTab: 'weekly' | 'daily') => {
        if (newTab === 'weekly') {
            navigate('/leaderboard/weekly');
        } else {
            navigate(dailyDate === getTodayUTC() ? '/leaderboard/daily' : `/leaderboard/daily/${dailyDate}`);
        }
    };

    const handlePrevDay = () => {
        const d = new Date(dailyDate + 'T00:00:00Z');
        d.setUTCDate(d.getUTCDate() - 1);
        const newDate = d.toISOString().slice(0, 10);
        setDailyDate(newDate);
        navigate(`/leaderboard/daily/${newDate}`);
    };

    const handleNextDay = () => {
        if (isDailyToday) return;
        const d = new Date(dailyDate + 'T00:00:00Z');
        d.setUTCDate(d.getUTCDate() + 1);
        const newDate = d.toISOString().slice(0, 10);
        setDailyDate(newDate);
        navigate(newDate === getTodayUTC() ? '/leaderboard/daily' : `/leaderboard/daily/${newDate}`);
    };

    const handleBackToToday = () => {
        const today = getTodayUTC();
        setDailyDate(today);
        navigate('/leaderboard/daily');
    };

    const filteredDailyVideos = dailyVideos.filter((v: any) => {
        if (!dailySearch.trim()) return true;
        const q = dailySearch.toLowerCase();
        return (v.title && v.title.toLowerCase().includes(q)) || (v.author && v.author.toLowerCase().includes(q));
    });

    /* ── Render ── */
    const podium = entries.slice(0, 3);
    const heights = ['200px', '160px', '130px'];

    const currentUserEntry = user ? entries.find(e => e.username.toLowerCase() === user.toLowerCase()) : null;
    const canClaim = rewardsEnabled && weekOffset === -1 && currentUserEntry && currentUserEntry.reward_amount! > 0 && !currentUserEntry.is_claimed;

    return (
        <div className="leaderboard-page">
            <SEO title={`${activeTab === 'weekly' ? 'Weekly' : 'Daily'} Leaderboard | Speem`} /> 

            {/* Tab Switcher */}
            <div className="lb-tab-switcher">
                <button 
                    className={`lb-tab-btn ${activeTab === 'weekly' ? 'active' : ''}`}
                    onClick={() => handleSwitchTab('weekly')}
                >
                    🏆 Weekly
                </button>
                <button 
                    className={`lb-tab-btn ${activeTab === 'daily' ? 'active' : ''}`}
                    onClick={() => handleSwitchTab('daily')}
                >
                    🔥 Daily
                </button>
            </div>

            {activeTab === 'weekly' ? (
                <>
                    <div className="lb-header">
                        <div className="lb-title-row">
                            <span className="lb-trophy">🏆</span>
                            <div>
                                <h1 className="lb-title">Weekly Leaderboard</h1>
                                <p className="lb-subtitle">Participate and win Steem rewards by watching and creating content.</p>
                            </div>
                        </div>

                        <div className="lb-week-nav">
                            <button 
                                className="lb-week-btn" 
                                onClick={() => setWeekOffset(w => w - 1)}
                                title="Previous week"
                            >
                                <i className="fas fa-chevron-left" />
                            </button>
                            <div className="lb-week-label">
                                {isCurrentWeek 
                                    ? (
                                        <div className="lb-nav-current">
                                            <div className="lb-status">Current Week</div>
                                            <div className="lb-timer text-muted" style={{ fontWeight: 300 }}>Ends in: {timeUntil}</div>
                                        </div>
                                    )
                                    : weekLabel}
                            </div>
                            <button 
                                className="lb-week-btn" 
                                disabled={isCurrentWeek}
                                onClick={() => setWeekOffset(w => w + 1)}
                                title="Next week"
                            >
                                <i className="fas fa-chevron-right" />
                            </button>
                        </div>
                    </div>

                    {/* Claim Reward Banner for Winners */}
                    {canClaim && (
                        <div className="lb-claim-banner animate__animated animate__fadeInUp mb-4">
                            <div className="d-flex align-items-center justify-content-between p-3 bg-speem-confirm" style={{ borderRadius: '20px' }}>
                                <div>
                                    <h4 className="mb-0">🎉 Congratulations @{user}!</h4>
                                    <p className="mb-0 small opacity-8">
                                        You won <strong>{currentUserEntry?.reward_amount?.toFixed(3)} {prizeSymbol}</strong> (Rank #{currentUserEntry?.rank})
                                    </p>
                                </div>
                                <Button 
                                    color="speem" 
                                    className="px-4" 
                                    disabled={isClaiming}
                                    onClick={handleClaim}
                                    style={{ background: 'var(--speem)', color: 'white', borderRadius: '15px' }}
                                >
                                    {isClaiming ? <i className="fas fa-spinner fa-spin mr-2" /> : <i className="fas fa-gift mr-2" />}
                                    Claim Prize
                                </Button>
                            </div>
                        </div>
                    )}
                    
                    {currentUserEntry?.is_claimed && weekOffset === -1 && (
                        <div className="lb-claimed-status text-center mb-4 p-2 bg-secondary2" style={{ borderRadius: '15px', fontSize: '0.85rem' }}>
                            <i className="fas fa-check-circle text-success mr-2" /> 
                            You successfully claimed your reward for this week!
                        </div>
                    )}

                    {/* Podium */}
                    {!isLoading && entries.length > 0 && isCurrentWeek && (
                        <div className="lb-podium">
                            {[1, 0, 2].map((visualIdx) => {
                                const entry = podium[visualIdx];
                                if (!entry) return <div key={visualIdx} className="lb-podium-slot empty" />;
                                const visualRank = visualIdx + 1;
                                return (
                                    <div key={entry.username} className={`lb-podium-slot rank-${visualRank}`}>
                                        <div className="lb-podium-user d-flex flex-column align-items-center w-100">
                                            <a href={`/u/${entry.username}`} className="lb-podium-avatar-link">
                                                <img 
                                                    src={`https://steemitimages.com/u/${entry.username}/avatar`} 
                                                    className="lb-podium-avatar" 
                                                    alt={entry.username} 
                                                />
                                            </a>
                                            <div className="lb-podium-name-wrapper d-flex align-items-center justify-content-center mt-2">
                                                <a href={`/u/${entry.username}`} className="lb-podium-name">
                                                    @{entry.username}
                                                </a>
                                                {entry.plan_type?.includes("plus") && (
                                                    <div className="ml-1" title="Verified Plus member">
                                                        <VerifiedIcon size={14} className="color-speem" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="lb-podium-score d-flex flex-column align-items-center text-center w-100">
                                                <div className="lb-podium-points">{entry.score.toLocaleString()} pts</div>
                                                
                                                {rewardsEnabled && entry.reward_amount! > 0 && (
                                                    <div className="lb-podium-reward small text-success d-flex align-items-center justify-content-center mt-1" style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                                        <img src="/assets/img/steem.webp" alt="Steem" style={{ width: '14px', height: '14px' }} className="mr-1" />
                                                        {entry.reward_amount?.toFixed(2)} {prizeSymbol}
                                                    </div>
                                                )}
                                            </div>
                                            <div 
                                                className={`lb-podium-base podium-${PODIUM_COLORS[visualRank - 1]} d-flex flex-column align-items-center justify-content-center`}
                                                style={{ height: heights[visualIdx] }}
                                            >
                                                <div className="lb-podium-stats mb-1">
                                                    <span title="Views received"><i className="fas fa-eye mr-1" />{entry.total_views}</span>
                                                    <span title="Videos published"><i className="fas fa-video mr-1" />{entry.video_count}</span>
                                                    <span title="Videos watched"><i className="fas fa-play-circle mr-1" />{entry.videos_watched}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Ranking Table Controls */}
                    <div className="lb-controls">
                        <div className="lb-search-wrap">
                            <i className="fas fa-search lb-search-icon" />
                            <input 
                                type="text" 
                                className="lb-search" 
                                placeholder="Find a user..." 
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="lb-counter">
                            <span className="lb-counter-num">{filtered.length}</span>
                            <span className="lb-counter-total">/{entries.length}</span>
                            <span className="lb-counter-label ml-1">users</span>
                        </div>
                    </div>

                    <AdSense type="horizontal" className="mb-4" />

                    <div className="lb-table-wrap">
                        <table className="lb-table">
                            <thead>
                                <tr>
                                    <th className="lb-rank-cell text-center">Rank</th>
                                    <th>User</th>
                                    <th 
                                        className={`lb-th-sort ${sortBy === 'score' ? 'active' : ''}`}
                                        onClick={() => setSortBy('score')}
                                    >
                                        Score {sortBy === 'score' && <i className="fas fa-caret-down ml-1" />}
                                    </th>
                                    <th 
                                        className={`lb-th-sort ${sortBy === 'total_views' ? 'active' : ''}`}
                                        onClick={() => setSortBy('total_views')}
                                    >
                                        Views {sortBy === 'total_views' && <i className="fas fa-caret-down ml-1" />}
                                    </th>
                                    <th 
                                        className={`lb-th-sort ${sortBy === 'video_count' ? 'active' : ''}`}
                                        onClick={() => setSortBy('video_count')}
                                    >
                                        Videos {sortBy === 'video_count' && <i className="fas fa-caret-down ml-1" />}
                                    </th>
                                    <th 
                                        className={`lb-th-sort ${sortBy === 'videos_watched' ? 'active' : ''}`}
                                        onClick={() => setSortBy('videos_watched')}
                                    >
                                        Watched {sortBy === 'videos_watched' && <i className="fas fa-caret-down ml-1" />}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    Array(10).fill(0).map((_, i) => (
                                        <tr key={i} className="lb-skeleton-wrap">
                                            <td colSpan={6}><Skeleton height={25} /></td>
                                        </tr>
                                    ))
                                ) : (
                                    filtered.map((entry) => (
                                        <tr key={entry.username} className={`lb-row ${entry.username.toLowerCase() === user?.toLowerCase() ? 'bg-speem-confirm' : ''} ${entry.rank <= 3 ? `lb-top-${entry.rank}` : ''}`}>
                                            <td className="lb-rank-cell text-center align-middle">
                                                {entry.rank <= 3 ? (
                                                    <span className="lb-medal">{MEDALS[entry.rank - 1]}</span>
                                                ) : (
                                                    <span className="lb-rank-num">{entry.rank}</span>
                                                )}
                                            </td>
                                            <td style={{ minWidth: '150px' }}>
                                                <div className="d-flex align-items-center" style={{ overflow: 'hidden' }}>
                                                    <a href={`/u/${entry.username}`} className="lb-avatar-link flex-shrink-0" style={{ display: 'flex' }}>
                                                        <img 
                                                            src={`https://steemitimages.com/u/${entry.username}/avatar`} 
                                                            className="lb-avatar mr-2" 
                                                            alt={entry.username} 
                                                            style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                                                        />
                                                    </a>
                                                    <div className="d-flex flex-column overflow-hidden">
                                                        <div className="d-flex align-items-center overflow-hidden">
                                                            <a href={`/u/${entry.username}`} className="lb-username font-weight-bold text-truncate" style={{ color: 'var(--letters)', textDecoration: 'none' }}>
                                                                @{entry.username}
                                                            </a>
                                                            {entry.plan_type?.includes("plus") && (
                                                                <span className="flex-shrink-0">
                                                                    <VerifiedIcon size={14} className="color-speem ml-1" />
                                                                </span>
                                                            )}
                                                        </div>
                                                        {rewardsEnabled && entry.reward_amount! > 0 && (
                                                            <div className="text-success d-flex align-items-center" style={{ fontSize: '0.7rem', fontWeight: 700 }}>
                                                                <img src="/assets/img/steem.webp" alt="Steem" style={{ width: '12px', height: '12px' }} className="mr-1 flex-shrink-0" />
                                                                <span className="text-truncate">+{entry.reward_amount?.toFixed(2)} {prizeSymbol}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="lb-score-cell font-weight-bold" title={SCORE_BREAKDOWN(entry)} style={{ color: 'var(--letters)' }}>
                                                    {entry.score.toLocaleString()}
                                                </span>
                                            </td>
                                            <td>{entry.total_views.toLocaleString()}</td>
                                            <td>{entry.video_count}</td>
                                            <td>{entry.videos_watched}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    
                    {!isLoading && filtered.length === 0 && (
                        <div className="lb-empty">
                            <span className="lb-empty-icon">📊</span>
                            No participants found for this period.
                        </div>
                    )}

                    <div className="lb-info-section mt-5 p-4 border shadow-sm" style={{ backgroundColor: 'var(--bg-light)', borderRadius: '20px', border: '1px solid var(--border-color)' }}>
                        <h4 className="mb-4 d-flex align-items-center" style={{ color: 'var(--letters)' }}>
                            <i className="fas fa-star mr-2 text-speem" />
                            Scoring System & Rewards
                        </h4>
                        <Row className="g-3">
                            <Col md="4" className="mb-3 mb-md-0">
                                <div className="p-4 text-center h-100 border" style={{ backgroundColor: 'var(--bg-main)', borderRadius: '15px' }}>
                                    <div className="h2 mb-1" style={{ color: 'var(--speem)', fontWeight: 'bold' }}>3</div>
                                    <div className="font-weight-bold mb-2" style={{ color: 'var(--letters)' }}>Points per View</div>
                                    <small className="text-muted">Earn points when other users watch your videos. Content creation pays off!</small>
                                </div>
                            </Col>
                            <Col md="4" className="mb-3 mb-md-0">
                                <div className="p-4 text-center h-100 border" style={{ backgroundColor: 'var(--bg-main)', borderRadius: '15px' }}>
                                    <div className="h2 mb-1" style={{ color: 'var(--speem)', fontWeight: 'bold' }}>5</div>
                                    <div className="font-weight-bold mb-2" style={{ color: 'var(--letters)' }}>Points per Video</div>
                                    <small className="text-muted">Get rewarded for each unique IPFS video you publish on the platform.</small>
                                </div>
                            </Col>
                            <Col md="4">
                                <div className="p-4 text-center h-100 border" style={{ backgroundColor: 'var(--bg-main)', borderRadius: '15px' }}>
                                    <div className="h2 mb-1" style={{ color: 'var(--speem)', fontWeight: 'bold' }}>0.5</div>
                                    <div className="font-weight-bold mb-2" style={{ color: 'var(--letters)' }}>Point per Watch</div>
                                    <small className="text-muted">Support other creators and earn points for your daily engagement.</small>
                                </div>
                            </Col>
                        </Row>
                        <div className="mt-4 pt-3 border-top text-center text-muted small" style={{ fontStyle: 'italic' }}>
                            * Rewards must be claimed manually within 7 days after the end of each week (UTC).
                        </div>
                    </div>
                </>
            ) : (
                /* ── Daily Videos Tab ── */
                <>
                    <div className="lb-header">
                        <div className="lb-title-row">
                            <span className="lb-trophy">🔥</span>
                            <div>
                                <h1 className="lb-title">Daily Highlight Videos</h1>
                                <p className="lb-subtitle">Top videos published on this day (UTC) ranked by views. Discover, curate, and engage!</p>
                            </div>
                        </div>

                        <div className="lb-week-nav">
                            <button 
                                className="lb-week-btn" 
                                onClick={handlePrevDay}
                                title="Previous day"
                            >
                                <i className="fas fa-chevron-left" />
                            </button>
                            <div className="lb-week-label">
                                {isDailyToday 
                                    ? (
                                        <div className="lb-nav-current">
                                            <div className="lb-status">Today (UTC)</div>
                                            <div className="lb-timer text-muted" style={{ fontWeight: 300 }}>Ends in: {dailyTimeUntil}</div>
                                        </div>
                                    )
                                    : (
                                        <div className="lb-nav-current">
                                            <div className="lb-status">{formatDailyDateLabel(dailyDate)}</div>
                                            <div 
                                                className="lb-timer text-muted" 
                                                style={{ cursor: 'pointer', textDecoration: 'underline' }} 
                                                onClick={handleBackToToday}
                                                title="Jump back to current day"
                                            >
                                                Back to Today
                                            </div>
                                        </div>
                                    )}
                            </div>
                            <button 
                                className="lb-week-btn" 
                                disabled={isDailyToday}
                                onClick={handleNextDay}
                                title="Next day"
                            >
                                <i className="fas fa-chevron-right" />
                            </button>
                        </div>
                    </div>

                    {/* Daily Search Controls */}
                    <div className="lb-controls">
                        <div className="lb-search-wrap">
                            <i className="fas fa-search lb-search-icon" />
                            <input 
                                type="text" 
                                className="lb-search" 
                                placeholder="Search daily videos by title or author..." 
                                value={dailySearch}
                                onChange={(e) => setDailySearch(e.target.value)}
                            />
                        </div>
                        <div className="lb-counter">
                            <span className="lb-counter-num">{filteredDailyVideos.length}</span>
                            <span className="lb-counter-total">/{dailyVideos.length}</span>
                            <span className="lb-counter-label ml-1">videos</span>
                        </div>
                    </div>

                    <AdSense type="horizontal" className="mb-4" />

                    {/* Daily Video Grid */}
                    {isDailyLoading ? (
                        <Row>
                            {Array(8).fill(0).map((_, i) => (
                                <Col key={i} xs="12" sm="6" md="4" lg="3" className="mb-4">
                                    <Skeleton height={180} style={{ borderRadius: '12px' }} />
                                    <div className="mt-2">
                                        <Skeleton height={20} />
                                        <Skeleton height={14} width="60%" />
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    ) : filteredDailyVideos.length > 0 ? (
                        <Row>
                            {filteredDailyVideos.map((video) => (
                                <Col key={video.id} xs="12" sm="6" md="4" lg="3" className="mb-4">
                                    <PostVideoItem 
                                        post={video.post || {
                                            author: video.author,
                                            permlink: (video.url || '').split('/')[2] || '',
                                            title: video.title || '',
                                            created: video.created,
                                            views: video.views,
                                            active_votes: []
                                        }} 
                                        isPlus={video.is_plus}
                                        rankNumber={video.rank}
                                        topWeeklyCount={video.top15_weekly_count}
                                    />
                                </Col>
                            ))}
                        </Row>
                    ) : (
                        <div className="lb-empty py-5 text-center">
                            <span className="lb-empty-icon" style={{ fontSize: '3rem' }}>📹</span>
                            <h4 className="mt-3">No videos found for this date</h4>
                            <p className="text-muted small">Try checking another date using the navigation controls above.</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default LeaderboardPage;
